import React, { useState, useCallback } from "react";
import styled from "@emotion/styled";

const SearchSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select",
  required = false,
  name,
  className,
  disabled = false,
  error,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSelect = useCallback(
    (option) => {
      onChange?.({ target: { name, value: option.value } });
      setIsOpen(false);
      setSearchTerm("");
    },
    [onChange, name]
  );

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => {
        const next = !prev;
        if (!next) setSearchTerm(""); // reset when closing
        return next;
      });
    }
  }, [disabled]);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().startsWith(searchTerm.toLowerCase())
  );

  return (
    <SelectContainer className={className}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <SelectButton
        onClick={toggleDropdown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        role="combobox"
        error={error}
      >
        <SelectValue isPlaceholder={!selectedOption}>
          {selectedOption ? selectedOption.label : placeholder}
        </SelectValue>
        <CaretIcon>
          <svg
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.28125 5.71875L1.28125 1.71875C1 1.4375 0.90625 1 1.0625 0.625C1.21875 0.25 1.59375 0 2 0H10C10.4062 0 10.75 0.25 10.9062 0.625C11.0625 1 10.9688 1.4375 10.6875 1.71875L6.6875 5.71875C6.3125 6.125 5.65625 6.125 5.28125 5.71875Z"
              fill="#64748B"
            />
          </svg>
        </CaretIcon>
      </SelectButton>

      {isOpen && (
        <OptionsMenu role="listbox">
          <SearchInput
            type="text"
            placeholder="Type to filter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = value === option.value;
              return (
                <OptionItem
                  key={option.value}
                  isSelected={isSelected}
                  isDisabled={option.isDisabled}
                  onClick={() => !option.isDisabled && handleSelect(option)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.isDisabled}
                >
                  <OptionText isSelected={isSelected}>
                    {option.label}
                  </OptionText>
                </OptionItem>
              );
            })
          ) : (
            <NoResults>No matching options</NoResults>
          )}
        </OptionsMenu>
      )}
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </SelectContainer>
  );
};

export default SearchSelect;

// ---------------------------------
// Styled components below
// ---------------------------------

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: left;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  text-align: left;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const SelectButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  height: 42px;
  background: #ffffff;
  border: solid 1px
    ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgb(97, 98, 197);
  }

  &:focus {
    outline: none;
    border-color: rgb(97, 98, 197);
  }
`;

const SelectValue = styled.span`
  color: ${(props) =>
    props.isPlaceholder ? "rgba(100, 116, 139, 1)" : "#000000"};
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
`;

const CaretIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionsMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #e0e4f4;
  border-radius: 8px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;

  &:focus {
    outline: none;
    border-color: rgb(97, 98, 197);
  }
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  background: ${(props) => (props.isSelected ? "#F1F5F9" : "transparent")};
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? "none" : "auto")};

  &:hover {
    background: ${(props) => (props.isDisabled ? "transparent" : "#F1F5F9")};
  }
`;

const OptionText = styled.span`
  color: ${(props) => (props.isSelected ? "#6162C5" : "#000000")};
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
`;

const NoResults = styled.div`
  padding: 12px 16px;
  color: #999;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  text-align: center;
`;
